import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Tooltip } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import logo from '../../assets/images/logo.png';
import { SignOutUser } from "../../firebase/firebase";
import { getUserFromLocalStorage } from "../../utils/helper";
import { getTenant, getTenants } from "../../utils/apis/tenants_repository";
import { getTenantAlerts, AlertsData } from "../../utils/apis/reports_repository";
import {
    LogOut,
    Clock,
    BookOpen,
    Building2,
    BarChart3,
    UserCircle,
    UsersRound,
    Car,
    CreditCard,
    Wallet,
    PanelLeftClose,
    PanelLeftOpen,
    ParkingCircle,
    ScrollText,
    AlertTriangle,
    Bell,
} from "lucide-react";

interface NavItem {
    title: string;
    icon: React.ReactNode;
    navigator: string;
}

interface NavSection {
    heading: string;
    items: NavItem[];
}

interface OccupancyStats {
    booked: number;
    guests: number;
    vip: number;
    available: number;
    total: number;
}

const OccupancyWidget = ({ collapsed }: { collapsed: boolean }) => {
    const user = getUserFromLocalStorage() ?? { role: "User" };

    const { data: stats } = useQuery<OccupancyStats>({
        queryKey: ['sidebarOccupancy', user.role, user.tenant],
        queryFn: async () => {
            if (user.role === "SuperAdmin") {
                const tenants = await getTenants();
                return tenants.reduce(
                    (acc, t) => ({
                        booked: acc.booked + (t.liveBookingOccupancy || 0),
                        guests: acc.guests + (t.liveGuestOccupancy || 0),
                        vip: acc.vip + (t.liveVIPOccupancy || 0),
                        available: acc.available + (t.availableOccupancy || 0),
                        total: acc.total + (t.totalAllocation || 0),
                    }),
                    { booked: 0, guests: 0, vip: 0, available: 0, total: 0 }
                );
            } else {
                const t = await getTenant(user.tenant);
                return {
                    booked: t.liveBookingOccupancy || 0,
                    guests: t.liveGuestOccupancy || 0,
                    vip: t.liveVIPOccupancy || 0,
                    available: t.availableOccupancy || 0,
                    total: t.totalAllocation || 0,
                };
            }
        },
        refetchInterval: 30000,
    });

    if (!stats) return null;

    const used = stats.booked + stats.guests + stats.vip;
    const pct = stats.total > 0 ? Math.round((used / stats.total) * 100) : 0;

    let barColor = "bg-green-500";
    if (pct >= 100) barColor = "bg-red-500";
    else if (pct >= 75) barColor = "bg-yellow-500";

    if (collapsed) {
        const ringColor = pct >= 100 ? "text-red-500" : pct >= 75 ? "text-yellow-500" : "text-green-500";
        return (
            <Tooltip title={`${used}/${stats.total} occupied (${pct}%)`} placement="right">
                <div className="flex flex-col items-center px-2 py-3">
                    <div className={`relative flex items-center justify-center ${ringColor}`}>
                        <ParkingCircle className="w-7 h-7" />
                    </div>
                </div>
            </Tooltip>
        );
    }

    const rows = [
        { label: "Booked", value: stats.booked, color: "bg-blue-500" },
        { label: "Guests", value: stats.guests, color: "bg-purple-500" },
        { label: "VIP", value: stats.vip, color: "bg-amber-500" },
        { label: "Available", value: stats.available, color: "bg-green-500" },
    ];

    return (
        <div className="px-3 pt-2 pb-3">
            <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2">Live Occupancy</p>
                <div className="mb-3">
                    <div className="flex justify-between text-[11px] mb-1">
                        <span className="font-medium text-gray-700">{used} / {stats.total}</span>
                        <span className={`font-semibold ${pct >= 100 ? 'text-red-600' : pct >= 75 ? 'text-yellow-600' : 'text-green-600'}`}>{pct}%</span>
                    </div>
                    <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                    </div>
                </div>
                <div className="space-y-1.5">
                    {rows.map(r => (
                        <div key={r.label} className="flex items-center justify-between text-[11px]">
                            <div className="flex items-center gap-1.5">
                                <div className={`w-2 h-2 rounded-full ${r.color}`} />
                                <span className="text-gray-600">{r.label}</span>
                            </div>
                            <span className="font-medium text-gray-700">{r.value}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

const AlertsWidget = ({ collapsed, onNavigate }: { collapsed: boolean; onNavigate: (path: string) => void }) => {
    const user = getUserFromLocalStorage() ?? { role: "User" };

    const { data: todayAlerts } = useQuery<AlertsData[]>({
        queryKey: ['sidebarAlerts', user.tenant],
        queryFn: async () => {
            const alerts = await getTenantAlerts(user.tenant);
            const now = new Date();
            const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
            const todayEnd = todayStart + 86400000;
            return alerts.filter(a => {
                const ts = a.entryTimestamp || a.alertTimestamp;
                if (!ts) return false;
                const t = new Date(ts).getTime();
                return !isNaN(t) && t >= todayStart && t < todayEnd;
            });
        },
        refetchInterval: 60000,
    });

    if (!todayAlerts || todayAlerts.length === 0) return null;

    if (collapsed) {
        return (
            <Tooltip title={`${todayAlerts.length} alert${todayAlerts.length !== 1 ? 's' : ''} today`} placement="right">
                <div
                    className="flex flex-col items-center px-2 py-3 cursor-pointer"
                    onClick={() => onNavigate('/reports')}
                >
                    <div className="relative flex items-center justify-center text-red-500">
                        <Bell className="w-6 h-6" />
                        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                            {todayAlerts.length}
                        </span>
                    </div>
                </div>
            </Tooltip>
        );
    }

    return (
        <div className="px-3 pt-1 pb-3">
            <div className="rounded-lg border border-red-100 bg-red-50 p-3">
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5">
                        <AlertTriangle className="w-3 h-3 text-red-500" />
                        <p className="text-[10px] font-semibold text-red-500 uppercase tracking-wider">Alerts Today</p>
                    </div>
                    <span className="bg-red-500 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                        {todayAlerts.length}
                    </span>
                </div>
                <div className="space-y-1.5 max-h-32 overflow-y-auto">
                    {todayAlerts.slice(0, 5).map((alert, i) => {
                        const ts = alert.entryTimestamp || alert.alertTimestamp || "";
                        const d = new Date(ts);
                        const time = !isNaN(d.getTime()) ? d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true }) : "";
                        return (
                            <div
                                key={i}
                                className="flex flex-col text-[11px] bg-white rounded px-2 py-1.5 border border-red-100 cursor-pointer hover:border-red-300 transition-colors"
                                onClick={() => onNavigate('/reports')}
                            >
                                <span className="font-medium text-gray-700 truncate">{alert.cardholderName}</span>
                                <div className="flex items-center justify-between">
                                    <span className="text-gray-400 truncate">{alert.cameraName}</span>
                                    {time && <span className="text-gray-400 text-[10px] shrink-0 ml-1">{time}</span>}
                                </div>
                            </div>
                        );
                    })}
                </div>
                {todayAlerts.length > 5 && (
                    <p
                        className="text-[10px] text-red-400 mt-1.5 text-center cursor-pointer hover:text-red-600"
                        onClick={() => onNavigate('/reports')}
                    >
                        +{todayAlerts.length - 5} more
                    </p>
                )}
            </div>
        </div>
    );
};

interface SideBarProps {
    collapsed: boolean;
    onToggle: () => void;
}

const SideBar = ({ collapsed, onToggle }: SideBarProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const currentPath = location.pathname;

    const user = getUserFromLocalStorage() ?? { role: "User" };
    const valetAllowedEmails = ["yusuf@ddilabs.ai", "gabe@ddilabs.ai"];
    const canViewValet = valetAllowedEmails.includes((user?.email ?? "").toLowerCase());

    const iconClass = "w-4 h-4 shrink-0";

    const sections: NavSection[] = (() => {
        if (user.role === "RestaurantUser") {
            return [
                {
                    heading: "Bookings",
                    items: [
                        { title: "Active Bookings", icon: <Clock className={iconClass} />, navigator: "/restaurant" },
                    ]
                },
                {
                    heading: "Payments",
                    items: [
                        { title: "Pay with Card", icon: <CreditCard className={iconClass} />, navigator: "/payment/card" },
                        { title: "Direct Debit", icon: <Wallet className={iconClass} />, navigator: "/payment/direct-debit" },
                    ]
                }
            ];
        }

        const base: NavSection[] = [
            {
                heading: "Bookings",
                items: [
                    { title: "Active Bookings", icon: <Clock className={iconClass} />, navigator: "/bookings" },
                    { title: "History", icon: <BookOpen className={iconClass} />, navigator: "/bookings/history" },
                ]
            },
            {
                heading: "Admin",
                items: [
                    { title: "Rules", icon: <ScrollText className={iconClass} />, navigator: "/rules" },
                    ...(user.role === "SuperAdmin" ? [{ title: "Tenants", icon: <Building2 className={iconClass} />, navigator: "/tenants" }] : []),
                    { title: "Reports", icon: <BarChart3 className={iconClass} />, navigator: "/reports" },
                ]
            },
            {
                heading: "Users",
                items: [
                    { title: "All Users", icon: <UserCircle className={iconClass} />, navigator: "/users" },
                    { title: "User Groups", icon: <UsersRound className={iconClass} />, navigator: "/usergroups" },
                ]
            },
            {
                heading: "Account",
                items: [
                    { title: "Profile", icon: <UserCircle className={iconClass} />, navigator: "/profile" },
                    ...(canViewValet ? [{ title: "Valet", icon: <Car className={iconClass} />, navigator: "/valet" }] : []),
                ]
            },
        ];

        return base;
    })();

    const handleNav = (navigator: string) => {
        navigate(navigator);
    };

    const handleLogout = async () => {
        await SignOutUser();
        navigate("/");
    };

    const isValetOrSubroute = (path: string) => {
        return path.startsWith('/valet');
    };

    if (isValetOrSubroute(currentPath)) {
        return null;
    }

    return (
        <div className={`${collapsed ? 'w-16' : 'w-52'} flex flex-col bg-white border-r border-gray-200 justify-between transition-all duration-200 shrink-0`}>
            <div className="flex flex-col overflow-y-auto justify-start">
                {/* Logo + collapse toggle */}
                <div className={`flex items-center ${collapsed ? 'justify-center px-2' : 'justify-between px-4'} py-4`}>
                    {!collapsed && <img src={logo} alt="logo" className="max-h-10 object-contain" />}
                    <button
                        onClick={onToggle}
                        className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        {collapsed ? <PanelLeftOpen className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
                    </button>
                </div>

                {/* Nav sections */}
                <nav className={`${collapsed ? 'px-2' : 'px-3'} mt-1 space-y-4`}>
                    {sections.map((section) => (
                        <div key={section.heading}>
                            {/* Section heading */}
                            {!collapsed ? (
                                <p className="px-3 mb-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                                    {section.heading}
                                </p>
                            ) : (
                                <div className="border-t border-gray-100 my-2" />
                            )}

                            {/* Section items - always visible */}
                            {section.items.map((item) => (
                                <Tooltip key={item.navigator} title={item.title} placement="right" disableHoverListener={!collapsed}>
                                    <div
                                        onClick={() => handleNav(item.navigator)}
                                        className={`${currentPath === item.navigator ? "bg-primary text-white" : "text-secondaryText hover:bg-gray-50"} text-sm flex items-center gap-2 ${collapsed ? 'justify-center px-2' : 'px-3'} py-2 my-0.5 rounded-md cursor-pointer transition-colors`}
                                    >
                                        {item.icon}
                                        {!collapsed && <span>{item.title}</span>}
                                    </div>
                                </Tooltip>
                            ))}
                        </div>
                    ))}
                </nav>

                {/* Live occupancy widget */}
                <OccupancyWidget collapsed={collapsed} />

                {/* Today's alerts */}
                <AlertsWidget collapsed={collapsed} onNavigate={handleNav} />
            </div>

            {/* Bottom section */}
            <div className={`${collapsed ? 'px-2' : 'px-3'} pb-3`}>
                <Tooltip title="Logout" placement="right" disableHoverListener={!collapsed}>
                    <div
                        className={`flex items-center gap-2 ${collapsed ? 'justify-center px-2' : 'px-3'} py-2 text-sm text-gray-500 hover:text-primary hover:bg-gray-50 rounded-md cursor-pointer transition-colors`}
                        onClick={handleLogout}
                    >
                        <LogOut className="w-4 h-4 shrink-0" />
                        {!collapsed && <span>Logout</span>}
                    </div>
                </Tooltip>
                {!collapsed && (
                    <p className="text-[10px] text-gray-400 px-3 mt-1">
                        v{process.env.REACT_APP_VERSION ? `${process.env.REACT_APP_VERSION}` : "-"}
                    </p>
                )}
            </div>
        </div>
    )
};

export default SideBar;
